<template>
  <div class="lease-ledger-edit-page">
    <div class="page-title">Lease Ledger Entry Edit</div>

    <!-- Toolbar -->
    <div class="ledger-toolbar">
      <select v-model="selectedYear" class="year-select">
        <option v-for="year in availableYears" :key="year" :value="year">
          {{ year }}
        </option>
      </select>
      <input
        ref="filterInput"
        v-model="filterText"
        type="text"
        placeholder="Filter by address, description, account... (ESC clears)"
        class="filter-input"
        @keydown.esc="
          filterText = '';
          sortField = '';
          sortOrder = 'asc';
        "
      />
      <button
        @click="editSelectedEntry"
        class="edit-btn"
        title="Edit selected row (or press 'e')"
        :disabled="!selectedEntryId"
      >
        <i class="fas fa-edit"></i>
      </button>
      <button @click="fetchLedgerEntries" class="refresh-btn" title="Refresh">
        <i class="fas fa-sync-alt"></i>
      </button>
      <button @click="printTable" class="print-btn" title="Print">
        <i class="fas fa-print"></i>
      </button>
      <button @click="exportToExcel" class="excel-btn" title="Export to Excel">
        <i class="fas fa-file-excel"></i>
      </button>
      <span v-if="selectedEntryId && showEditHint" class="edit-hint">
        press 'e' key to edit
      </span>
    </div>

    <!-- Table -->
    <div
      class="ledger-table-wrapper"
      @mouseenter="tableHovered = true"
      @mouseleave="tableHovered = false"
    >
      <table class="ledger-table">
        <thead>
          <tr>
            <th
              style="width: 150px"
              class="sortable-th"
              @click="sortBy('address')"
            >
              Address
              <i
                class="fas"
                :class="{
                  'fa-sort': sortField !== 'address',
                  'fa-sort-up': sortField === 'address' && sortOrder === 'asc',
                  'fa-sort-down':
                    sortField === 'address' && sortOrder === 'desc',
                }"
              ></i>
            </th>
            <th style="width: 75px">Ends On</th>
            <th style="width: 140px">Tenants</th>
            <th style="width: 90px" class="sortable-th" @click="sortBy('date')">
              Date
              <i
                class="fas"
                :class="{
                  'fa-sort': sortField !== 'date',
                  'fa-sort-up': sortField === 'date' && sortOrder === 'asc',
                  'fa-sort-down': sortField === 'date' && sortOrder === 'desc',
                }"
              ></i>
            </th>
            <th style="width: 150px">Description</th>
            <th style="width: 80px; text-align: right">Credit</th>
            <th style="width: 80px; text-align: right">Debit</th>
            <th style="width: 70px" class="sortable-th" @click="sortBy('type')">
              Type
              <i
                class="fas"
                :class="{
                  'fa-sort': sortField !== 'type',
                  'fa-sort-up': sortField === 'type' && sortOrder === 'asc',
                  'fa-sort-down': sortField === 'type' && sortOrder === 'desc',
                }"
              ></i>
            </th>
            <th
              style="width: 150px"
              class="sortable-th"
              @click="sortBy('account')"
            >
              Account
              <i
                class="fas"
                :class="{
                  'fa-sort': sortField !== 'account',
                  'fa-sort-up': sortField === 'account' && sortOrder === 'asc',
                  'fa-sort-down':
                    sortField === 'account' && sortOrder === 'desc',
                }"
              ></i>
            </th>
            <th style="width: 150px">Note</th>
            <th style="width: 130px">Modified</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(entry, index) in filteredEntries"
            :key="entry.id"
            @click="selectedEntryId = entry.id"
            @dblclick="handleDoubleClick($event, entry)"
            :class="{
              'selected-row': selectedEntryId === entry.id,
              'odd-row': index % 2 === 1,
              'even-row': index % 2 === 0,
            }"
          >
            <td
              class="address-cell"
              title="Double-click to open Lease Search"
              v-html="highlightSearch(entry.address)"
            ></td>
            <td>{{ formatDateLong(entry.endsOn) }}</td>
            <td
              class="tenants-cell"
              :title="entry.tenants"
              v-html="highlightSearch(entry.tenants)"
            ></td>
            <td>{{ formatDate(entry.date) }}</td>
            <td
              class="description-cell"
              :title="entry.description"
              v-html="highlightSearch(entry.description)"
            ></td>
            <td style="text-align: right">
              {{ entry.credit ? formatNumber(entry.credit) : '' }}
            </td>
            <td style="text-align: right">
              {{ entry.debit ? formatNumber(entry.debit) : '' }}
            </td>
            <td v-html="highlightSearch(getTypeLabel(entry.type))"></td>
            <td
              class="account-cell"
              :title="entry.account"
              v-html="highlightSearch(entry.account)"
            ></td>
            <td
              class="note-cell"
              :title="entry.note"
              v-html="highlightSearch(entry.note)"
            ></td>
            <td>{{ formatDate(entry.modifiedOn) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Summary -->
    <div class="ledger-summary">
      Total: {{ filteredEntries.length }} entries | Credits:
      {{ formatNumber(totalCredits) }} | Debits: {{ formatNumber(totalDebits) }}
    </div>

    <!-- Edit Dialog (cloned from LeaseSearch LedgerPopup) -->
    <div v-if="showEditDialog" class="ledger-popup">
      <div class="ledger-popup-content">
        <div class="popup-header">
          <span>Edit Ledger Entry - {{ editingEntry?.address }}</span>
          <button
            style="background: none"
            class="close-button"
            @click="closeEditDialog"
          >
            <i class="fas fa-close"></i>
          </button>
        </div>

        <div class="form">
          <div class="form-row">
            <label>Date</label>
            <input ref="editDate" v-model="form.date" type="date" />
          </div>

          <div class="form-row">
            <label for="description">Description</label>
            <select
              v-model="selectedPredefined"
              @blur="updateDescription"
              @change="updateDescription"
            >
              <option value="" disabled>Select a description</option>
              <option
                v-for="(option, index) in predefinedOptions"
                :key="index"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </div>

          <div class="form-row" v-if="selectedPredefined === 'Other...'">
            <label for="description">Description</label>
            <input
              ref="formDescription"
              v-model="form.description"
              placeholder="Enter your description"
              type="text"
            />
          </div>

          <div class="form-row">
            <label>Credit</label>
            <input v-model="form.credit" placeholder="0.00" type="number" />
          </div>

          <div class="form-row">
            <label>Debit</label>
            <input v-model="form.debit" placeholder="0.00" type="number" />
          </div>

          <div class="form-row">
            <label>Transaction No.</label>
            <input
              v-model="form.num"
              placeholder="transaction number"
              type="text"
            />
          </div>

          <div class="form-row">
            <label>Type</label>
            <select v-model="form.type">
              <option value="x">Choose...</option>
              <option value="0">Other</option>
              <option value="1">Fee</option>
              <option value="2">Credit</option>
              <option value="3">Refund</option>
              <option value="4">Writeoff</option>
            </select>
          </div>

          <div class="form-row">
            <label>Account</label>
            <select v-model="form.account">
              <option :value="null"></option>
              <option
                v-for="acct in accountOptions"
                :key="acct.id"
                :value="acct.value"
              >
                {{ acct.label }}
              </option>
            </select>
          </div>

          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <div class="popup-footer">
            <button @click="saveEntry" class="save-btn">Save</button>
            <button @click="closeEditDialog" class="cancel-btn">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay when dialog is open -->
    <div v-if="showEditDialog" class="overlay" @click="closeEditDialog"></div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default {
  name: 'LeaseLedgerEntryEdit',

  data() {
    return {
      ledgerEntries: [],
      filterText: '',
      selectedYear: new Date().getFullYear() - 1,
      sortField: '',
      sortOrder: 'asc',
      selectedEntryId: null,
      tableHovered: false,
      showEditHint: false,
      editHintTimeout: null,
      showEditDialog: false,
      editingEntry: null,
      errorMessage: '',
      accountOptions: [],
      predefinedOptions: [
        'Security Deposit',
        'Security Deposit Refund',
        'Court Fee',
        'Transfer to Subsequent Lease',
        'overpayment refund',
        'ending balance writeoff',
        'Early resign discount',
        'Late fee',
        'Manual Pmt entry',
        'Opening balance from prior lease',
        'Offset for rejected DEX payment',
        'Parking fee',
        'Other...',
      ],
      selectedPredefined: '',
      form: {
        date: '',
        description: '',
        credit: null,
        debit: null,
        num: '',
        type: 'x',
        account: null,
      },
    };
  },

  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= 2020; y--) {
        years.push(y);
      }
      return years;
    },

    filteredEntries() {
      let entries = this.ledgerEntries;

      // Apply year filter
      if (this.selectedYear) {
        entries = entries.filter((e) => {
          if (!e.date) return false;
          const entryYear = new Date(e.date).getFullYear();
          return entryYear === this.selectedYear;
        });
      }

      // Apply filter with support for negation (!) and comma-separated AND tokens
      if (this.filterText) {
        const tokens = this.filterText
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0);

        for (const token of tokens) {
          const isNegation = token.startsWith('!');
          const search = (isNegation ? token.slice(1) : token).toLowerCase();
          if (!search) continue;

          entries = entries.filter((e) => {
            const matchesAny =
              (e.address && e.address.toLowerCase().includes(search)) ||
              (e.tenants && e.tenants.toLowerCase().includes(search)) ||
              (e.description && e.description.toLowerCase().includes(search)) ||
              (e.account && e.account.toLowerCase().includes(search)) ||
              (e.note && e.note.toLowerCase().includes(search)) ||
              this.getTypeLabel(e.type).toLowerCase().includes(search);
            return isNegation ? !matchesAny : matchesAny;
          });
        }
      }

      // Apply sort
      if (this.sortField) {
        entries = [...entries].sort((a, b) => {
          let aVal = a[this.sortField] || '';
          let bVal = b[this.sortField] || '';

          // Handle date field
          if (this.sortField === 'date') {
            if (!aVal && !bVal) return 0;
            if (!aVal) return 1; // blanks at end
            if (!bVal) return -1;
            aVal = new Date(aVal).getTime();
            bVal = new Date(bVal).getTime();
          } else if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
          }

          if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return entries;
    },

    totalCredits() {
      return this.filteredEntries.reduce(
        (sum, e) => sum + (parseFloat(e.credit) || 0),
        0,
      );
    },

    totalDebits() {
      return this.filteredEntries.reduce(
        (sum, e) => sum + (parseFloat(e.debit) || 0),
        0,
      );
    },
  },

  watch: {
    selectedEntryId(newVal) {
      if (newVal) {
        this.showEditHint = true;
        if (this.editHintTimeout) clearTimeout(this.editHintTimeout);
        this.editHintTimeout = setTimeout(() => {
          this.showEditHint = false;
        }, 3000);
      }
    },
  },

  mounted() {
    document.title = 'Lease Ledger Edit';
    this.fetchLedgerEntries();
    this.fetchAccountOptions();
    window.addEventListener('keydown', this.handleKeydown);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
    if (this.editHintTimeout) clearTimeout(this.editHintTimeout);
  },

  methods: {
    async fetchLedgerEntries() {
      try {
        const response = await axios.get('/api/lease-ledger-all');
        this.ledgerEntries = response.data;
      } catch (error) {
        console.error('Error fetching ledger entries:', error);
      }
    },

    async fetchAccountOptions() {
      try {
        const response = await axios.get(
          '/api/chart-of-accounts/ledger-accounts',
        );
        this.accountOptions = response.data.map((acct) => ({
          id: acct.id,
          value: `${acct.accountNumber} : ${acct.accountName}`,
          label: `${acct.accountNumber} : ${acct.accountName}`,
        }));
      } catch (error) {
        console.error('Error fetching account options:', error);
      }
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortOrder = 'asc';
      }
    },

    handleKeydown(event) {
      if (
        event.key === 'e' &&
        this.tableHovered &&
        this.selectedEntryId &&
        !this.showEditDialog
      ) {
        this.editSelectedEntry();
      }
    },

    handleDoubleClick(event, entry) {
      const target = event.target;
      // Check if clicked on address cell
      if (target.classList.contains('address-cell')) {
        // Open LeaseSearch in a new tab with the leaseId
        // Use 'search' param with ':leaseId' format to auto-select the lease
        window.open(`/lease-search?search=:${entry.leaseId}`, '_blank');
      } else {
        // Edit the entry
        this.editEntry(entry);
      }
    },

    editSelectedEntry() {
      const entry = this.ledgerEntries.find(
        (e) => e.id === this.selectedEntryId,
      );
      if (entry) {
        this.editEntry(entry);
      }
    },

    editEntry(entry) {
      this.editingEntry = entry;
      this.errorMessage = '';

      // Populate form
      this.form.date = entry.date ? entry.date.slice(0, 10) : '';
      this.form.description = entry.description || '';
      this.form.credit = entry.credit;
      this.form.debit = entry.debit;
      this.form.num = entry.transactionNum || '';
      this.form.type = entry.type !== null ? String(entry.type) : 'x';
      this.form.account = entry.account || null;

      // Set predefined dropdown
      if (this.predefinedOptions.includes(entry.description)) {
        this.selectedPredefined = entry.description;
      } else {
        this.selectedPredefined = 'Other...';
      }

      this.showEditDialog = true;

      this.$nextTick(() => {
        if (this.$refs.editDate) {
          this.$refs.editDate.focus();
        }
      });
    },

    updateDescription() {
      if (this.selectedPredefined && this.selectedPredefined !== 'Other...') {
        this.form.description = this.selectedPredefined;
      }
    },

    closeEditDialog() {
      this.showEditDialog = false;
      this.editingEntry = null;
      this.errorMessage = '';
      this.selectedPredefined = '';
      this.form = {
        date: '',
        description: '',
        credit: null,
        debit: null,
        num: '',
        type: 'x',
        account: null,
      };
    },

    async saveEntry() {
      // Validation
      if (!this.form.date || isNaN(Date.parse(this.form.date))) {
        this.errorMessage = 'Please enter a valid date.';
        return;
      }
      if (!this.form.description || this.form.description.trim() === '') {
        this.errorMessage = 'Description is required.';
        return;
      }
      if (this.form.credit != null && this.form.credit < 0) {
        this.errorMessage = 'Credit must be a non-negative number.';
        return;
      }
      if (this.form.debit != null && this.form.debit < 0) {
        this.errorMessage = 'Debit must be a non-negative number.';
        return;
      }
      if (this.form.credit == null && this.form.debit == null) {
        this.errorMessage =
          'Either credit or debit must be a non-negative number.';
        return;
      }
      if (this.form.type === 'x' || this.form.type === null) {
        this.errorMessage = 'Please select a valid type.';
        return;
      }

      try {
        await axios.post('/api/lease-ledger/update', {
          ...this.form,
          leaseId: this.editingEntry.leaseId,
          ledgerId: this.editingEntry.id,
        });

        this.closeEditDialog();
        this.fetchLedgerEntries();
      } catch (error) {
        console.error('Error updating ledger entry:', error);
        this.errorMessage = 'Failed to save. Please try again.';
      }
    },

    getTypeLabel(type) {
      const types = {
        0: 'Other',
        1: 'Fee',
        2: 'Credit',
        3: 'Refund',
        4: 'Writeoff',
      };
      return types[type] || '';
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(
        d.getDate(),
      ).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
    },

    formatDateLong(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(
        d.getDate(),
      ).padStart(2, '0')}/${d.getFullYear()}`;
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(
        d.getDate(),
      ).padStart(
        2,
        '0',
      )}/${String(d.getFullYear()).slice(-2)} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
    },

    formatNumber(value) {
      if (value === null || value === undefined) return '';
      return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    highlightSearch(text) {
      if (!text) return '';
      if (!this.filterText) return text;

      // Get positive search terms only (exclude negations)
      const positiveTerms = this.filterText
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0 && !t.startsWith('!'))
        .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

      if (positiveTerms.length === 0) return text;

      const regex = new RegExp(`(${positiveTerms.join('|')})`, 'gi');
      return text.replace(regex, '<span class="search-highlight">$1</span>');
    },

    printTable() {
      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Lease Ledger Entries</title>
          <style type="text/css">
            @page { size: landscape; }
            @media print {
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            body { font-family: Arial, sans-serif; font-size: 8pt; margin: 10px; }
            h2 { font-size: 12pt; margin-bottom: 10px; }
            table { border-collapse: collapse; width: 100%; table-layout: fixed; }
            th, td { border: 1px solid #ccc; padding: 3px 5px; text-align: left; font-size: 8pt; overflow: hidden; }
            th { background-color: #888 !important; color: #fff !important; }
            .even-row { background-color: #e0e0e0 !important; }
            .odd-row { background-color: #ffffff !important; }
            .right { text-align: right; }
            .col-address { width: 120px; }
            .col-ends { width: 70px; }
            .col-tenants { width: 100px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .col-date { width: 65px; }
            .col-desc { width: 120px; }
            .col-credit { width: 60px; }
            .col-debit { width: 60px; }
            .col-type { width: 60px; }
            .col-account { width: auto; }
            .footer { text-align: center; font-size: 8pt; color: #888; margin-top: 30px; }
          </style>
        </head>
        <body>
        <h2>Lease Ledger Entries - ${new Date().toLocaleDateString()}</h2>
        <table>
          <thead>
            <tr>
              <th class="col-address">Address</th>
              <th class="col-ends">Ends On</th>
              <th class="col-tenants">Tenants</th>
              <th class="col-date">Date</th>
              <th class="col-desc">Description</th>
              <th class="col-credit right">Credit</th>
              <th class="col-debit right">Debit</th>
              <th class="col-type">Type</th>
              <th class="col-account">Account</th>
            </tr>
          </thead>
          <tbody>
            ${this.filteredEntries
              .map(
                (e, i) => `
              <tr class="${i % 2 === 0 ? 'odd-row' : 'even-row'}">
                <td class="col-address">${e.address || ''}</td>
                <td class="col-ends">${this.formatDateLong(e.endsOn)}</td>
                <td class="col-tenants" title="${e.tenants || ''}">${e.tenants || ''}</td>
                <td class="col-date">${this.formatDate(e.date)}</td>
                <td class="col-desc">${e.description || ''}</td>
                <td class="col-credit right">${e.credit ? this.formatNumber(e.credit) : ''}</td>
                <td class="col-debit right">${e.debit ? this.formatNumber(e.debit) : ''}</td>
                <td class="col-type">${this.getTypeLabel(e.type)}</td>
                <td class="col-account">${e.account || ''}</td>
              </tr>
            `,
              )
              .join('')}
          </tbody>
        </table>
        <div class="footer">
          <p>Pixie Properties</p>
          <p>${new Date().toLocaleString()}</p>
        </div>
        </body>
        </html>
      `;
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    },

    exportToExcel() {
      const data = this.filteredEntries.map((e) => ({
        Address: e.address,
        EndsOn: this.formatDateLong(e.endsOn),
        Tenants: e.tenants,
        Date: this.formatDate(e.date),
        Description: e.description,
        Credit: e.credit,
        Debit: e.debit,
        Type: this.getTypeLabel(e.type),
        Account: e.account,
        Note: e.note,
        Modified: this.formatDateTime(e.modifiedOn),
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ledger Entries');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([wbout], { type: 'application/octet-stream' }),
        `LedgerEntries_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    },
  },
};
</script>

<style scoped>
.lease-ledger-edit-page {
  padding: 10px;
  max-width: 1175px;
}

.page-title {
  font-size: 1.5em;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

.ledger-toolbar {
  display: flex;
  gap: 3px;
  margin-bottom: 10px;
  align-items: center;
}

.year-select {
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  background: #fff;
}

.filter-input {
  flex: 1;
  max-width: 300px;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
}

.ledger-toolbar button {
  height: 23px;
  border-radius: 4px;
  padding: 1px;
  border: 1px solid #878787;
  margin-left: 3px;
  margin-right: 3px;
  background: #bbb;
  font-size: 0.8rem;
  width: 26px;
  cursor: pointer;
}

.ledger-toolbar button:hover:not(:disabled) {
  background: #999;
}

.ledger-toolbar button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.edit-hint {
  font-size: 12px;
  color: #666;
  margin-left: 10px;
  animation: fadeOut 8s ease-in-out forwards;
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  75% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.ledger-table-wrapper {
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.ledger-table-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.ledger-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 9pt;
  line-height: 1.2;
}

.ledger-table th {
  background: #888;
  color: #fff;
  padding: 2px 3px;
  text-align: left;
  border-bottom: 1px solid #ccc;
  border-left: 1px solid #ccc;
  position: sticky;
  top: 0;
  z-index: 1;
}

.ledger-table td {
  padding: 1px 3px;
  border-bottom: 0.1em solid #e4e4e4;
  border-left: 1pt solid #ccc;
}

.ledger-table tbody tr:hover {
  background: #f5f5f5;
  cursor: pointer;
}

.selected-row {
  background-color: #baa1bd !important;
}

.odd-row {
  background-color: #ddd;
}

.even-row {
  background-color: #fff;
}

.sortable-th {
  cursor: pointer;
  user-select: none;
}

.sortable-th:hover {
  background: #666;
}

.sortable-th i {
  margin-left: 3px;
  font-size: 0.8em;
}

.address-cell {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.address-cell:hover {
  text-decoration: underline;
}

.tenants-cell {
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.description-cell {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-cell {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.note-cell {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ledger-summary {
  margin-top: 10px;
  padding: 8px;
  background: #f8f8f8;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
}

/* Edit Dialog Styles (cloned from LeaseSearch) */
.ledger-popup {
  position: fixed;
  top: 25%;
  left: 30px;
  width: 400px;
  z-index: 9999;
  background-color: white;
  border: 2px solid #ccc;
  padding: 20px;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.5);
  border-radius: 5px;
}

.popup-header {
  margin: -22px;
  padding: 8px;
  background: #333;
  color: #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  padding-left: 12px;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9em;
  color: #eee;
}

.form {
  margin-left: 30px;
  margin-top: 35px;
}

.form-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.form-row label {
  width: 30%;
  text-align: left;
  margin-top: 12px;
  font-size: 0.9em;
}

.form-row input,
.form-row select {
  width: 65%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.popup-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  gap: 10px;
}

.popup-footer button {
  flex-grow: 1;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
}

.save-btn {
  background: #4b2e83;
  color: white;
  border-color: #4b2e83 !important;
}

.save-btn:hover {
  background: #3a236a;
}

.cancel-btn {
  background: #f5f5f5;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.error-message {
  margin-top: 15px;
  color: rgb(215, 72, 72);
  font-size: 0.9em;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
}

:deep(.search-highlight) {
  background: #ecdcf2;
  color: #555;
}
</style>
